import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { PasswordInput } from './ui/password-input';
import {
  CheckCircle,
  AlertCircle,
  Info,
  KeyRound,
  Mail,
  ShieldCheck,
  Loader2,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
type Step = 'request' | 'email-sent' | 'set-password' | 'token-invalid' | 'done';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

/**
 * Extract the tenant subdomain from the current hostname.
 * Returns null on root domain (pathkit.pro) or localhost.
 * E.g. "acme-corp.pathkit.pro" → "acme-corp"
 */
function getTenantSubdomain(): string | null {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return null;
  const parts = host.split('.');
  // Need at least 3 parts: subdomain.pathkit.pro
  if (parts.length < 3) return null;
  const subdomain = parts.slice(0, -2).join('.');
  // Ignore reserved subdomains
  const reserved = ['www', 'app', 'api', 'admin', 'demo'];
  if (reserved.includes(subdomain)) return null;
  return subdomain;
}

/**
 * Secure token-based password reset.
 *
 * If the URL contains ?token=..., the component validates that token on mount
 * and renders the password form. Otherwise it shows the "request reset" form.
 */
export function PasswordReset() {
  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1 — request
  const [email, setEmail] = useState('');

  // Step 2 — set password (token present in URL)
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Current step
  const [step, setStep] = useState<Step>('request');

  // ---------------------------------------------------------------------------
  // On mount: check for ?token= in the URL
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');

    if (urlToken && urlToken.length >= 32) {
      setToken(urlToken);
      setLoading(true);
      validateToken(urlToken);

      // Remove token from the URL bar for security
      const cleanUrl =
        window.location.pathname +
        window.location.search
          .replace(/[?&]token=[^&]+/, '')
          .replace(/^\?$/, '');
      window.history.replaceState({}, '', cleanUrl || window.location.pathname);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Validate token on mount
  // ---------------------------------------------------------------------------
  const validateToken = async (rawToken: string) => {
    try {
      const subdomain = getTenantSubdomain();
      const subdomainParam = subdomain ? `&subdomain=${encodeURIComponent(subdomain)}` : '';
      const response = await fetch(
        `${API_BASE}/auth/validate-reset-token?token=${encodeURIComponent(rawToken)}${subdomainParam}`,
        {
          headers: bearerAuthHeader(),
        }
      );

      const data = await response.json();

      if (data.valid) {
        setStep('set-password');
      } else {
        setTokenError(data.error || 'This reset link is invalid or has expired.');
        setStep('token-invalid');
      }
    } catch (err: any) {
      console.error('[PasswordReset] Token validation error:', err);
      setTokenError('Unable to validate the reset link. Please try again.');
      setStep('token-invalid');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Step 1: Request a reset link
  // ---------------------------------------------------------------------------
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...bearerAuthHeader(),
        },
        body: JSON.stringify({ email }),
      });

      // The server always returns 200 with a generic message
      await response.json();
      setStep('email-sent');
    } catch (err: any) {
      console.error('[PasswordReset] Request error:', err);
      setError('A network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Step 2: Submit new password
  // ---------------------------------------------------------------------------
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number.');
      return;
    }

    setLoading(true);

    try {
      const subdomain = getTenantSubdomain();
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...bearerAuthHeader(),
        },
        body: JSON.stringify({
          token,
          newPassword,
          ...(subdomain ? { subdomain } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Password reset failed. The link may have expired.');
        setLoading(false);
        return;
      }

      setStep('done');
    } catch (err: any) {
      console.error('[PasswordReset] Reset error:', err);
      setError('A network error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading state (token validation in progress)
  // ---------------------------------------------------------------------------
  if (step === 'request' && loading && token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Validating reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            <CardTitle className="text-xl">Reset Password</CardTitle>
          </div>
          <CardDescription>
            {step === 'request' && 'Enter your email to receive a password reset link.'}
            {step === 'email-sent' && 'Check your inbox for the reset link.'}
            {step === 'set-password' && 'Choose a new password for your account.'}
            {step === 'token-invalid' && 'This reset link is no longer valid.'}
            {step === 'done' && 'Your password has been updated.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* ---- STEP: Request reset ---- */}
          {step === 'request' && (
            <>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Enter the email address associated with your account.
                  If an account exists, you will receive a reset link.
                </AlertDescription>
              </Alert>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    disabled={loading}
                  />
                </div>

                <Button type="submit" className="w-full bg-primary text-white hover:bg-primary/90" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* ---- STEP: Email sent confirmation ---- */}
          {step === 'email-sent' && (
            <>
              <div className="flex flex-col items-center text-center gap-4 py-4">
                <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-green-600" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm">
                    If an account exists for <strong>{email}</strong>, a password
                    reset link has been sent. The link expires in 15 minutes.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Check your spam or junk folder if you do not see the email.
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setStep('request');
                  setEmail('');
                  setError('');
                }}
              >
                Send Another Link
              </Button>
            </>
          )}

          {/* ---- STEP: Set new password ---- */}
          {step === 'set-password' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <PasswordInput
                  id="newPassword"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={loading}
                />
              </div>

              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground mb-1">Password requirements:</p>
                <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>
                    One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                    One number
                  </li>
                </ul>
              </div>

              <Button type="submit" className="w-full bg-primary text-white hover:bg-primary/90" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating Password...
                  </>
                ) : (
                  'Set New Password'
                )}
              </Button>
            </form>
          )}

          {/* ---- STEP: Token invalid / expired ---- */}
          {step === 'token-invalid' && (
            <>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {tokenError}
                </AlertDescription>
              </Alert>

              <Button
                className="w-full"
                onClick={() => {
                  setStep('request');
                  setToken('');
                  setTokenError('');
                  setError('');
                }}
              >
                Request a New Reset Link
              </Button>
            </>
          )}

          {/* ---- STEP: Done ---- */}
          {step === 'done' && (
            <div className="flex flex-col items-center text-center gap-4 py-4">
              <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-green-600" />
              </div>
              <div className="space-y-1">
                <p className="text-sm">
                  Your password has been updated successfully. You may now sign
                  in with your new credentials.
                </p>
              </div>
              <Button
                asChild
                className="w-full bg-primary text-white hover:bg-primary/90"
              >
                <a href="/">Go to Sign In</a>
              </Button>
            </div>
          )}

          {/* ---- Inline error ---- */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ---- Footer link ---- */}
          {step !== 'done' && (
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground text-center">
                <a href="/" className="underline hover:text-foreground">
                  Back to Sign In
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}